/**
 * The clearing's controls (decision 0015), between the game's own bars while the clearing
 * shows in place of the expedition (Daniel, 2026-10-19): a tray on the bottom bar with undo, the
 * kinds of things and pictures of the things themselves, and a menu on a held tile to edit or
 * clear what stands there (Daniel, 2026-10-18). Editing opens the thing in its kind, to turn it or
 * swap it for another. The game's interface holds the rest: Arrange and Photo (`photo.ts`) in the
 * bottom bar, and the row of the player's clearings under the top bar, where a held clearing's
 * menu holds its size (proposed 2026-10-22). Paper, ink and 2 px lines like the
 * rest of the interface (decision 0013). Only what the player has found can be set out (Daniel,
 * 2026-10-17); the rest stands in the tray in its place, by name, not yet shown (Daniel,
 * 2026-10-20), so each kind tells how much is left to find.
 */
import type { Coord } from "@mistsweeper/core";
import { HOME } from "@mistsweeper/renderer";
import {
  CATEGORIES,
  type CatalogEntry,
  type Category,
  type ClearingState,
  canBeHome,
  clearable,
  clearTile,
  type Edit,
  GROUNDS,
  type Ground,
  groundAt,
  homeChoices,
  inside,
  itemAt,
  newlyShown,
  paint,
  place,
  type Refusal,
  setReach,
  turnAt,
} from "../clearing.ts";
import { type Locale, type StringKey, t } from "../i18n.ts";
import { getJson, setJson } from "../storage.ts";
import { esc, icon, wheelSideways } from "./dom.ts";
import { PhotoMode } from "./photo.ts";
import "./clearing.css";

/** Kept once the player closes the hint with its cross, so it does not come back. */
const HINT_KEY = "clearing.hint";
const UNDO_LIMIT = 60;
/** How far the menu stands from the finger that held the tile, in CSS pixels. */
const MENU_GAP = 18;

/** A thing's name as the tray gives it: its kind, and its number when its kind has several. */
export function entryName(locale: Locale, entry: CatalogEntry): string {
  const kind = t(locale, `clearing.family.${entry.family}` as StringKey);
  return entry.variant > 0 ? `${kind} ${entry.variant}` : kind;
}

export type Tool =
  | { readonly kind: "ground"; readonly ground: Ground }
  | { readonly kind: "clear" }
  | { readonly kind: "item"; readonly name: string };

type Tab = "ground" | Category;

/**
 * What the player did in the clearing that sounds by itself (decision 0022): a thing set out, a
 * ground painted, a thing turned, a tile cleared, what could not be done, the cabin touched, a
 * picture taken or shared.
 */
export type ClearingSound =
  | { readonly kind: "place"; readonly name: string }
  | { readonly kind: "ground"; readonly ground: Ground }
  | { readonly kind: "turn" | "clear" | "refused" | "cabin" | "shutter" | "share" };

const REFUSALS: Readonly<Record<Refusal, StringKey>> = {
  mist: "clearing.mist",
  cabin: "clearing.cabin",
  needsWater: "clearing.needsWater",
  needsLand: "clearing.needsLand",
};

export interface ClearingCallbacks {
  /** The clearing changed: keep it and draw it again. `shown` are the tiles the mist has just left. */
  changed(state: ClearingState, shown: readonly Coord[]): void;
  /** A field note, as the game shows one. */
  note(text: string): void;
  /** The photo mode opened or closed, for the camera in the bar. */
  photo(open: boolean): void;
  /** A picture of a model or of a ground (`ground:meadow`) for the tray; null without one. */
  thumbnail(key: string): Promise<string | null>;
  /** These pictures first: the tab the player is looking at. */
  prioritize(keys: readonly string[]): void;
  /**
   * The tile the world marks with the dashed square: the held tile while its menu shows, so the
   * player sees which tile it is for (Daniel, 2026-10-26), else the tile being edited; null for
   * none.
   */
  mark(tile: Coord | null): void;
  /** The world inside a rectangle of the screen in CSS pixels, for the photo mode. */
  capture(rect: DOMRect): HTMLCanvasElement | null;
  /** The player chose their own cabin (decision 0029): keep it and draw it; null is the first. */
  home(name: string | null): void;
  /** The hint that the cabin can be changed was closed, or followed: it does not come again. */
  cabinHintSeen(): void;
  /** What the player did sounds, in place of the tap of the control that did it. */
  sound(what: ClearingSound): void;
}

export class ClearingEditor {
  private state: ClearingState;
  /** The clearing being arranged, by its id among the player's clearings (`clearings.ts`). */
  private id: number;
  private history: ClearingState[] = [];
  /** The undo of the other clearings, kept while the page is open, so each undoes its own. */
  private readonly histories = new Map<number, ClearingState[]>();
  private tool: Tool | null = null;
  private tab: Tab = "ground";
  /** Whether the drawer of things shows under the row of kinds. */
  private drawerOpen = true;
  private cascadeTimer: ReturnType<typeof setTimeout> | null = null;
  private editingTile: Coord | null = null;
  private menuTile: Coord | null = null;
  /** The house in the cabin's place: the first cabin unless the player chose another. */
  private home = HOME;
  /** Whether the hint over the tray says the cabin can be changed, until it is closed. */
  private cabinHint = false;
  private readonly thumbs = new Map<string, string>();
  /** Counts the times the pictures were thrown away, so a late one of the old set is not kept. */
  private pictures = 0;
  private readonly locale: Locale;
  private entries: readonly CatalogEntry[];
  private byName: ReadonlyMap<string, CatalogEntry>;
  private readonly callbacks: ClearingCallbacks;
  private readonly el: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly hintText: HTMLElement;
  private readonly tabs: HTMLElement;
  private readonly chips: HTMLElement;
  /** The things' scrollbar for a mouse, and the bar in it that stands for the part in view. */
  private readonly scroll: HTMLElement;
  private readonly scrollBar: HTMLElement;
  private readonly tray: HTMLElement;
  /** How much of the open kind is found, a line over its things: "11 of 35 found". */
  private readonly count: HTMLElement;
  private readonly undoButton: HTMLButtonElement;
  private readonly menu: HTMLElement;
  private readonly menuName: HTMLElement;
  private readonly menuEdit: HTMLButtonElement;
  private readonly menuClear: HTMLButtonElement;
  private readonly menuCabin: HTMLButtonElement;
  private readonly editBar: HTMLElement;
  private readonly editName: HTMLElement;
  private readonly editTurn: HTMLButtonElement;
  private readonly editClear: HTMLButtonElement;
  private readonly photo: PhotoMode;
  private readonly onResize = () => {
    this.syncScroll();
    this.measureDrawer();
  };

  /** Builds the controls in `host`, which stands in the page under the game's interface. */
  constructor(
    host: HTMLElement,
    locale: Locale,
    id: number,
    state: ClearingState,
    entries: readonly CatalogEntry[],
    callbacks: ClearingCallbacks,
  ) {
    this.locale = locale;
    this.id = id;
    this.state = state;
    this.entries = entries;
    this.byName = new Map(entries.map((e) => [e.name, e]));
    this.callbacks = callbacks;
    const tr = (key: StringKey) => esc(this.t(key));
    this.el = document.createElement("div");
    this.el.className = "clearing";
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="clearing-menu" role="menu" hidden>
        <p class="kicker" data-menu-name></p>
        <button type="button" role="menuitem" data-menu="edit">${tr("clearing.edit")}</button>
        <button type="button" role="menuitem" data-menu="clear">${tr("clearing.clear")}</button>
        <button type="button" role="menuitem" data-menu="cabin">${tr("clearing.changeCabin")}</button>
      </div>
      <div class="clearing-tray">
        <div class="hint clearing-hint"><p>${tr("clearing.hint")}</p><button type="button" class="clearing-hint-close" aria-label="${tr("button.close")}">${icon("close", 18)}</button></div>
        <button type="button" class="clearing-fold" aria-label="${tr("clearing.fold")}">${icon("chevronDown", 24)}</button>
        <div class="clearing-edit" hidden>
          <p class="kicker" data-edit-name></p>
          <button type="button" data-edit="turn">${tr("clearing.turn")}</button>
          <button type="button" data-edit="clear">${tr("clearing.clear")}</button>
          <button type="button" data-edit="done">${tr("clearing.done")}</button>
        </div>
        <div class="clearing-row">
          <button type="button" class="clearing-undo">${tr("clearing.undo")}</button>
          <div class="clearing-tabs"></div>
        </div>
        <p class="kicker clearing-count"></p>
        <div class="clearing-chips"></div>
        <div class="clearing-scroll" aria-hidden="true"><div class="clearing-scroll-bar"></div></div>
      </div>`;
    host.append(this.el);
    const q = <T extends HTMLElement>(sel: string) => this.el.querySelector(sel) as T;
    this.hint = q(".clearing-hint");
    this.hintText = q(".clearing-hint p");
    if (getJson<string>(HINT_KEY) === "closed") this.hint.hidden = true;
    this.tabs = q(".clearing-tabs");
    this.chips = q(".clearing-chips");
    this.scroll = q(".clearing-scroll");
    this.scrollBar = q(".clearing-scroll-bar");
    this.tray = q(".clearing-tray");
    this.count = q(".clearing-count");
    this.undoButton = q(".clearing-undo");
    this.menu = q(".clearing-menu");
    this.menuName = q("[data-menu-name]");
    this.menuEdit = q('[data-menu="edit"]');
    this.menuClear = q('[data-menu="clear"]');
    this.menuCabin = q('[data-menu="cabin"]');
    this.editBar = q(".clearing-edit");
    this.editName = q("[data-edit-name]");
    this.editTurn = q('[data-edit="turn"]');
    this.editClear = q('[data-edit="clear"]');

    for (const tab of ["ground", ...CATEGORIES] as const) {
      const b = document.createElement("button");
      b.type = "button";
      b.dataset.tab = tab;
      b.textContent = this.t(`clearing.cat.${tab}` as StringKey);
      this.tabs.append(b);
    }
    this.photo = new PhotoMode(this.el, locale, {
      capture: (rect) => callbacks.capture(rect),
      done: () => this.leavePhoto(),
      sound: (kind) => callbacks.sound({ kind }),
    });
    this.el.addEventListener("click", (e) => this.onClick(e));
    addEventListener("resize", this.onResize);
    wheelSideways(this.tabs);
    wheelSideways(this.chips);
    this.chips.addEventListener("scroll", () => this.syncScroll(), { passive: true });
    this.scroll.addEventListener("pointerdown", (e) => this.dragScroll(e));
    this.render();
    this.wantAll();
  }

  /** The controls over the clearing, as the player comes to it. */
  show(): void {
    this.el.hidden = false;
    // The tray had no size while it was set aside.
    this.syncScroll();
    this.measureDrawer();
  }

  /** Sets the controls aside while the expedition shows; the clearing waits as it is. */
  hide(): void {
    // A hint not closed comes again on the next visit.
    if (this.cabinHint) this.restoreHint();
    this.leavePhoto();
    if (this.editingTile) this.stopEditing();
    this.dismissMenu();
    this.el.hidden = true;
  }

  /** Takes the controls away for good, as when the language changes and they are made anew. */
  destroy(): void {
    this.photo.hide();
    if (this.cascadeTimer !== null) clearTimeout(this.cascadeTimer);
    removeEventListener("resize", this.onResize);
    this.el.remove();
  }

  /** What can be set out now that more has been found on the expeditions. */
  setEntries(entries: readonly CatalogEntry[]): void {
    this.entries = entries;
    this.byName = new Map(entries.map((e) => [e.name, e]));
    this.render();
    this.wantAll();
  }

  /**
   * The hint over the tray says the cabin can be any house found (decision 0029), and stays until
   * its cross closes it or the player changes the cabin (Daniel, 2026-10-26: a note went too fast).
   */
  showCabinHint(): void {
    this.cabinHint = true;
    this.hintText.textContent = this.t("clearing.cabinHint");
    this.hint.classList.add("cabin");
    this.hint.hidden = false;
  }

  /** The cabin's hint closed or followed: it does not come again. */
  private endCabinHint(): void {
    if (!this.cabinHint) return;
    this.restoreHint();
    this.callbacks.cabinHintSeen();
  }

  /** The hint over the tray back to the usual one, set aside for this visit. */
  private restoreHint(): void {
    this.cabinHint = false;
    this.hintText.textContent = this.t("clearing.hint");
    this.hint.classList.remove("cabin");
    this.hint.hidden = true;
  }

  /** The player's own cabin as the settings keep it (decision 0029); null is the first cabin. */
  setHome(name: string | null): void {
    this.home = name ?? HOME;
    this.render();
  }

  /** Every picture drawn again, as after a change of theme. */
  reloadPictures(): void {
    this.pictures += 1;
    this.thumbs.clear();
    this.render();
    this.wantAll();
  }

  /**
   * Another of the player's clearings to arrange, chosen in the row under the top bar: editing
   * and the menu end, the chosen thing stays chosen, and undo goes back through that clearing's
   * own steps.
   */
  switchTo(id: number, state: ClearingState): void {
    if (id === this.id && state === this.state) return;
    if (id !== this.id) {
      this.histories.set(this.id, this.history);
      this.history = this.histories.get(id) ?? [];
      this.histories.delete(id);
      this.id = id;
    }
    this.state = state;
    this.dismissMenu();
    if (this.editingTile) this.stopEditing();
    else this.render();
  }

  /** A clearing deleted: its undo goes with it. */
  forget(id: number): void {
    this.histories.delete(id);
  }

  /** The clearing small or large, from the menu of a held clearing in the row. */
  setSize(reach: number): void {
    const { state, shown } = setReach(this.state, reach);
    if (state === this.state) return;
    this.commit(state, shown);
    if (shown.length > 0) this.showNote(this.t("clearing.grown"));
  }

  /** Photo in the bottom bar opens the photo mode, Arrange goes back to arranging. */
  setPhoto(on: boolean): void {
    if (on) this.enterPhoto();
    else this.leavePhoto();
  }

  get current(): ClearingState {
    return this.state;
  }

  /** The tile being edited, or null. */
  get editing(): Coord | null {
    return this.editingTile;
  }

  /** Whether the photo mode is open, where the world only moves under a finger. */
  get photographing(): boolean {
    return this.photo.open;
  }

  /**
   * A tap on a tile of the world. With the menu open it only closes the menu; while editing it
   * moves the editing to that tile; otherwise the chosen tool acts there, or the thing standing
   * there is named. `at` is the point of the ground tapped, in tiles, so a massif stands on the
   * corner nearest the finger.
   */
  tap(tile: Coord, at?: { readonly x: number; readonly y: number }): void {
    if (this.photo.open) return;
    if (this.menuTile) {
      this.dismissMenu();
      return;
    }
    const { x, y } = tile;
    if (this.editingTile) {
      if (x === 0 && y === 0) {
        if (!isCabin(this.editingTile)) {
          this.showNote(this.t("clearing.cabin"));
          this.callbacks.sound({ kind: "refused" });
        }
      } else if (!inside(this.state, x, y)) this.stopEditing();
      else this.startEditing(tile);
      return;
    }
    const tool = this.tool;
    if (!tool) {
      const item = itemAt(this.state, x, y);
      if (item) this.showNote(this.nameOf(item.name));
      if (isCabin(tile)) this.callbacks.sound({ kind: "cabin" });
      return;
    }
    const sound = this.toolSound(tool, tile);
    let edit: Edit;
    if (tool.kind === "ground") edit = paint(this.state, x, y, tool.ground);
    else if (tool.kind === "clear") edit = clearTile(this.state, x, y);
    else edit = place(this.state, x, y, tool.name, at ? { at } : {});
    if ("refused" in edit) {
      this.showNote(this.t(REFUSALS[edit.refused]));
      this.callbacks.sound({ kind: "refused" });
      return;
    }
    if (edit.state !== this.state) {
      this.callbacks.sound(sound);
      this.commit(edit.state, []);
    }
  }

  /**
   * A held tile: the menu to edit or clear what stands there, or on the cabin to change it
   * (decision 0029), by the finger at `at` in CSS px, the tile marked while it shows. Tells
   * whether the menu opened.
   */
  hold(tile: Coord, at: { readonly x: number; readonly y: number }): boolean {
    if (this.photo.open) return false;
    const { x, y } = tile;
    if (!inside(this.state, x, y)) {
      this.showNote(this.t("clearing.mist"));
      this.callbacks.sound({ kind: "refused" });
      return false;
    }
    const cabin = isCabin(tile);
    if (cabin) this.callbacks.sound({ kind: "cabin" });
    this.menuTile = { x, y };
    this.menuName.textContent = cabin ? this.nameOf(this.home) : this.tileName(tile);
    this.menuEdit.hidden = cabin;
    this.menuClear.hidden = cabin || !clearable(this.state, x, y);
    this.menuCabin.hidden = !cabin;
    this.menu.hidden = false;
    // Above the finger, so it is not under it; below when there is no room above. The controls
    // span the room between the game's bars, so the menu is placed within it.
    const room = this.el.getBoundingClientRect();
    const w = this.menu.offsetWidth;
    const h = this.menu.offsetHeight;
    const fx = at.x - room.left;
    const fy = at.y - room.top;
    const trayTop = this.tray.getBoundingClientRect().top - room.top;
    const left = Math.min(Math.max(8, fx - w / 2), room.width - w - 8);
    let y0 = fy - h - MENU_GAP;
    if (y0 < 8) y0 = fy + MENU_GAP;
    y0 = Math.min(y0, trayTop - h - 8);
    this.menu.style.left = `${Math.round(left)}px`;
    this.menu.style.top = `${Math.round(Math.max(8, y0))}px`;
    this.showMark();
    return true;
  }

  dismissMenu(): void {
    if (!this.menuTile) return;
    this.menuTile = null;
    this.menu.hidden = true;
    this.showMark();
  }

  /** The world marks the held tile while its menu shows, else the tile being edited. */
  private showMark(): void {
    this.callbacks.mark(this.menuTile ?? this.editingTile);
  }

  /** Escape: closes the menu, then ends editing, then lets go of the chosen tool. */
  escape(): void {
    if (this.photo.open) this.photo.escape();
    else if (this.menuTile) this.dismissMenu();
    else if (this.editingTile) this.stopEditing();
    else if (this.tool) {
      this.tool = null;
      this.render();
    }
  }

  private t(key: StringKey, params: Record<string, string | number> = {}): string {
    return t(this.locale, key, params);
  }

  private nameOf(name: string): string {
    if (name === HOME) return this.t("clearing.family.house");
    const entry = this.byName.get(name);
    return entry ? entryName(this.locale, entry) : name;
  }

  /** What stands on a tile, or its ground when nothing does. */
  private tileName(tile: Coord): string {
    const item = itemAt(this.state, tile.x, tile.y);
    if (item) return this.nameOf(item.name);
    return this.t(`clearing.ground.${groundAt(this.state, tile.x, tile.y)}` as StringKey);
  }

  private commit(next: ClearingState, shown: readonly Coord[]): void {
    this.history.push(this.state);
    if (this.history.length > UNDO_LIMIT) this.history.shift();
    this.state = next;
    if (!this.cabinHint) this.hint.hidden = true;
    this.callbacks.changed(next, shown);
    this.render();
  }

  private undo(): void {
    const before = this.history.pop();
    if (!before) return;
    const shown = newlyShown(this.state, before);
    this.state = before;
    if (this.editingTile) this.tool = this.toolOn(this.editingTile);
    this.callbacks.changed(before, shown);
    this.render();
  }

  /** The tool that matches a tile: what stands there, or else its ground; the cabin's house. */
  private toolOn(tile: Coord): Tool {
    if (isCabin(tile)) return { kind: "item", name: this.home };
    const item = itemAt(this.state, tile.x, tile.y);
    return item
      ? { kind: "item", name: item.name }
      : { kind: "ground", ground: groundAt(this.state, tile.x, tile.y) };
  }

  /** Opens what stands on a tile in its kind, chosen, to turn it or swap it; or its ground. */
  private startEditing(tile: Coord): void {
    this.editingTile = { x: tile.x, y: tile.y };
    const item = itemAt(this.state, tile.x, tile.y);
    this.tab = isCabin(tile)
      ? "houses"
      : ((item && this.byName.get(item.name)?.category) ?? "ground");
    this.tool = this.toolOn(tile);
    const opening = !this.drawerOpen;
    this.drawerOpen = true;
    if (isCabin(tile)) this.endCabinHint();
    else if (!this.cabinHint) this.hint.hidden = true;
    this.showMark();
    this.render();
    if (opening) this.cascade();
    this.chips
      .querySelector('[aria-pressed="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "center" });
  }

  private stopEditing(): void {
    this.editingTile = null;
    this.tool = null;
    this.showMark();
    this.render();
  }

  /** While editing, a chosen tool acts on the edited tile at once. */
  private applyToEditing(tool: Tool): void {
    const tile = this.editingTile;
    if (!tile) return;
    if (isCabin(tile)) {
      if (tool.kind === "item" && (tool.name === HOME || canBeHome(tool.name))) {
        this.tool = tool;
        this.callbacks.sound({ kind: "place", name: tool.name });
        this.chooseHome(tool.name);
      }
      return;
    }
    this.tool = tool;
    const sound = this.toolSound(tool, tile);
    let edit: Edit;
    if (tool.kind === "ground") edit = paint(this.state, tile.x, tile.y, tool.ground);
    else if (tool.kind === "clear") edit = clearTile(this.state, tile.x, tile.y);
    else edit = place(this.state, tile.x, tile.y, tool.name, { keepTurn: true });
    if ("refused" in edit) {
      this.showNote(this.t(REFUSALS[edit.refused]));
      this.callbacks.sound({ kind: "refused" });
      this.render();
    } else if (edit.state !== this.state) {
      this.callbacks.sound(sound);
      this.commit(edit.state, []);
    } else this.render();
  }

  /**
   * What a tool sounds like on a tile: a ground painted, a tile cleared, a thing set out, or turned
   * where the same thing already stands, as `place` does.
   */
  private toolSound(tool: Tool, tile: Coord): ClearingSound {
    if (tool.kind === "ground") return { kind: "ground", ground: tool.ground };
    if (tool.kind === "clear") return { kind: "clear" };
    const standing = itemAt(this.state, tile.x, tile.y);
    return standing?.name === tool.name ? { kind: "turn" } : { kind: "place", name: tool.name };
  }

  /** The player's own cabin is now a house they have found, or the first cabin again. */
  private chooseHome(name: string): void {
    this.home = name;
    this.callbacks.home(name === HOME ? null : name);
    this.showNote(this.t("clearing.cabinChosen", { name: this.nameOf(name) }));
    this.render();
  }

  /** A turn or a clearing from the menu or the bar over the tray, and its sound. */
  private act(edit: Edit, sound: "turn" | "clear"): void {
    if ("refused" in edit) {
      this.showNote(this.t(REFUSALS[edit.refused]));
      this.callbacks.sound({ kind: "refused" });
    } else if (edit.state !== this.state) {
      this.callbacks.sound({ kind: sound });
      this.commit(edit.state, []);
    }
  }

  /** Sets the tray aside for the viewfinder. */
  private enterPhoto(): void {
    if (this.photo.open) return;
    if (this.editingTile) this.stopEditing();
    this.dismissMenu();
    this.el.classList.add("photo-on");
    this.photo.show();
    this.callbacks.photo(true);
  }

  private leavePhoto(): void {
    if (!this.photo.open) return;
    this.photo.hide();
    this.el.classList.remove("photo-on");
    // The tray had no size while it was set aside.
    this.measureDrawer();
    this.callbacks.photo(false);
  }

  private onClick(e: MouseEvent): void {
    // The photo mode handles its own buttons.
    if ((e.target as HTMLElement).closest(".photo")) return;
    const target = (e.target as HTMLElement).closest("button");
    if (!target) {
      if (this.menuTile && !(e.target as HTMLElement).closest(".clearing-menu")) this.dismissMenu();
      return;
    }
    if (target.dataset.menu) {
      const tile = this.menuTile;
      this.dismissMenu();
      if (!tile) return;
      if (target.dataset.menu === "edit" || target.dataset.menu === "cabin") {
        this.startEditing(tile);
      } else this.act(clearTile(this.state, tile.x, tile.y), "clear");
      return;
    }
    if (this.menuTile) this.dismissMenu();
    if (target.dataset.edit) {
      const tile = this.editingTile;
      if (!tile) return;
      if (target.dataset.edit === "turn") this.act(turnAt(this.state, tile.x, tile.y), "turn");
      else if (target.dataset.edit === "clear") {
        this.act(clearTile(this.state, tile.x, tile.y), "clear");
        this.tool = this.toolOn(tile);
        this.render();
      } else this.stopEditing();
    } else if (target.classList.contains("clearing-undo")) {
      this.undo();
    } else if (target.classList.contains("clearing-hint-close")) {
      if (this.cabinHint) {
        this.endCabinHint();
        return;
      }
      this.hint.hidden = true;
      setJson(HINT_KEY, "closed");
    } else if (target.classList.contains("clearing-fold")) {
      this.drawerOpen = false;
      this.render();
    } else if (target.dataset.tab) {
      // A kind opens the drawer; the chosen kind once more folds it away (Daniel, 2026-10-18).
      const tab = target.dataset.tab as Tab;
      // The houses the cabin can be stand in their tab alone; another kind ends the choosing.
      if (isCabin(this.editingTile) && tab !== this.tab) this.stopEditing();
      const opening = !this.drawerOpen;
      if (this.drawerOpen && tab === this.tab) this.drawerOpen = false;
      else {
        this.tab = tab;
        this.drawerOpen = true;
      }
      this.render();
      if (opening) this.cascade();
    } else if (target.dataset.locked) {
      this.showNote(this.t("clearing.locked"));
    } else if (target.dataset.tool) {
      const tool = this.toolFrom(target.dataset.tool);
      if (!tool) return;
      if (this.editingTile) {
        this.applyToEditing(tool);
        return;
      }
      this.tool = this.toolId(tool) === this.toolId(this.tool) ? null : tool;
      this.render();
    }
  }

  private toolFrom(id: string): Tool | null {
    if (id === "clear") return { kind: "clear" };
    if (id.startsWith("ground:")) {
      const ground = id.slice(7) as Ground;
      return GROUNDS.includes(ground) ? { kind: "ground", ground } : null;
    }
    if (id.startsWith("item:")) return { kind: "item", name: id.slice(5) };
    return null;
  }

  private toolId(tool: Tool | null): string {
    if (!tool) return "";
    if (tool.kind === "clear") return "clear";
    return tool.kind === "ground" ? `ground:${tool.ground}` : `item:${tool.name}`;
  }

  private showNote(text: string): void {
    this.callbacks.note(text);
  }

  /** Every picture, the shown tab's first, so other tabs open with theirs ready. */
  private wantAll(): void {
    for (const key of [
      ...GROUNDS.map((g) => `ground:${g}`),
      ...this.entries.map((e) => (e.found ? e.name : lockedKey(e.name))),
    ]) {
      this.want(key);
    }
  }

  /** Asks for a picture once, and puts it in every chip that shows its key when it comes. */
  private want(key: string): void {
    if (this.thumbs.has(key)) return;
    this.thumbs.set(key, "");
    const set = this.pictures;
    void this.callbacks.thumbnail(key).then((url) => {
      if (!url || set !== this.pictures) return;
      this.thumbs.set(key, url);
      for (const span of this.chips.querySelectorAll<HTMLElement>(".thumb")) {
        if (span.dataset.thumb === key && !span.firstChild) span.append(this.image(url));
      }
    });
  }

  private image(url: string): HTMLImageElement {
    const img = document.createElement("img");
    img.src = url;
    img.alt = "";
    return img;
  }

  /**
   * The things' scrollbar, for a mouse with no sideways scroll (Daniel, 2026-10-19): it shows
   * where the row runs past the window, and its bar stands for the part in view.
   */
  private syncScroll(): void {
    const row = this.chips;
    const room = row.scrollWidth - row.clientWidth;
    this.scroll.classList.toggle("needed", room > 1);
    const width = this.scroll.clientWidth;
    if (room <= 1 || width === 0) return;
    const bar = Math.max(32, (width * row.clientWidth) / row.scrollWidth);
    this.scrollBar.style.width = `${bar}px`;
    this.scrollBar.style.transform = `translateX(${((width - bar) * row.scrollLeft) / room}px)`;
  }

  /** Dragging the bar scrolls the things; a press beside it brings the bar's middle there first. */
  private dragScroll(e: PointerEvent): void {
    if (e.button !== 0) return;
    const row = this.chips;
    const track = this.scroll.getBoundingClientRect();
    const bar = this.scrollBar.getBoundingClientRect();
    const free = track.width - bar.width;
    const room = row.scrollWidth - row.clientWidth;
    if (free <= 0 || room <= 0) return;
    e.preventDefault();
    const grab = e.target === this.scrollBar ? e.clientX - bar.left : bar.width / 2;
    const to = (x: number) => {
      row.scrollLeft = ((x - track.left - grab) / free) * room;
    };
    to(e.clientX);
    this.scroll.setPointerCapture(e.pointerId);
    this.scroll.classList.add("dragging");
    const drag = new AbortController();
    const stop = () => {
      drag.abort();
      this.scroll.classList.remove("dragging");
    };
    this.scroll.addEventListener("pointermove", (m) => to(m.clientX), { signal: drag.signal });
    this.scroll.addEventListener("pointerup", stop, { signal: drag.signal });
    this.scroll.addEventListener("pointercancel", stop, { signal: drag.signal });
  }

  /** How far the tray slides down to fold: from the row of kinds to the tray's foot. */
  private measureDrawer(): void {
    const row = this.tray.querySelector<HTMLElement>(".clearing-row");
    const tray = this.tray.getBoundingClientRect();
    if (!row || tray.height === 0) return;
    const drawer = tray.bottom - row.getBoundingClientRect().bottom;
    this.tray.style.setProperty("--drawer", `${Math.round(drawer)}px`);
  }

  /** The things rise into place one after another as the drawer opens, as the menu's rows do. */
  private cascade(): void {
    if (this.cascadeTimer !== null) clearTimeout(this.cascadeTimer);
    this.tray.classList.add("cascade");
    this.cascadeTimer = setTimeout(() => {
      this.tray.classList.remove("cascade");
      this.cascadeTimer = null;
    }, 900);
  }

  private render(): void {
    this.undoButton.disabled = this.history.length === 0;
    for (const b of this.tabs.querySelectorAll<HTMLButtonElement>("button")) {
      b.setAttribute("aria-pressed", String(b.dataset.tab === this.tab));
    }
    this.tray.classList.toggle("folded", !this.drawerOpen);

    const editing = this.editingTile;
    const cabin = isCabin(editing);
    this.editBar.hidden = editing === null;
    if (editing) {
      const item = itemAt(this.state, editing.x, editing.y);
      this.editName.textContent = cabin
        ? this.t("clearing.chooseCabin")
        : this.t("clearing.editing", { name: this.tileName(editing) });
      this.editTurn.hidden = cabin || item === null;
      this.editClear.hidden = cabin || !clearable(this.state, editing.x, editing.y);
    }

    const selected = this.toolId(this.tool);
    const keys: string[] = [];
    let n = 0;
    const chip = (id: string, label: string, key: string | null) => {
      if (key) keys.push(key);
      const i = Math.min(n++, 8);
      const url = key ? this.thumbs.get(key) : "";
      const thumb = key
        ? `<span class="thumb" data-thumb="${esc(key)}">${url ? `<img src="${esc(url)}" alt="">` : ""}</span>`
        : "";
      return `<button type="button" data-tool="${esc(id)}" aria-pressed="${id === selected}" style="--i:${i}"${key ? "" : ' class="plain"'}>${thumb}<span class="label">${esc(label)}</span></button>`;
    };
    // A thing not found yet stands in its place by name, its picture a silhouette in the mist as
    // what stands hidden in the world (Daniel, 2026-10-20); a tap says it waits out there.
    const locked = (e: CatalogEntry) => {
      const key = lockedKey(e.name);
      keys.push(key);
      const i = Math.min(n++, 8);
      const name = this.nameOf(e.name);
      const url = this.thumbs.get(key);
      const thumb = `<span class="thumb" data-thumb="${esc(key)}">${url ? `<img src="${esc(url)}" alt="">` : ""}</span>`;
      return `<button type="button" class="locked" data-locked="${esc(e.name)}" aria-label="${esc(this.t("clearing.lockedName", { name }))}" style="--i:${i}">${thumb}<span class="label">${esc(name)}</span></button>`;
    };
    let html = "";
    let count = "";
    if (cabin && this.tab === "houses") {
      // The first cabin, and every house found that can be the cabin (decision 0029).
      html = [HOME, ...homeChoices(this.entries).map((e) => e.name)]
        .map((name) => chip(`item:${name}`, this.nameOf(name), name))
        .join("");
    } else if (this.tab === "ground") {
      html = GROUNDS.map((g) =>
        chip(`ground:${g}`, this.t(`clearing.ground.${g}` as StringKey), `ground:${g}`),
      )
        .concat(chip("clear", this.t("clearing.clear"), null))
        .join("");
    } else {
      const inTab = this.entries.filter((e) => e.category === this.tab);
      html = inTab
        .map((e) => (e.found ? chip(`item:${e.name}`, this.nameOf(e.name), e.name) : locked(e)))
        .join("");
      count = this.t("clearing.discovered", {
        n: inTab.filter((e) => e.found).length,
        total: inTab.length,
      });
    }
    this.chips.innerHTML = html;
    this.count.textContent = count;
    for (const key of keys) this.want(key);
    this.callbacks.prioritize(keys);
    this.syncScroll();
    this.measureDrawer();
  }
}

/** Whether a tile is the cabin's, which a held tile's menu changes rather than edits. */
function isCabin(tile: Coord | null): boolean {
  return tile !== null && tile.x === 0 && tile.y === 0;
}

/** The picture of a thing not found yet: its silhouette in the mist. */
function lockedKey(name: string): string {
  return `ghost:${name}`;
}
